import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Checkbox, CircularProgress, FormControlLabel, Stack, Typography } from '@mui/material';
import { shippingApi } from './api';

// Mounted only after explicit administrator review. The parent keys by order/operation.
export default function BuyerMessageReview({ operationKey, orderId, csrfToken, onAccessLost, onClose }) {
  const [preview, setPreview] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [loaded, setLoaded] = useState([]);
  const [confirmed, setConfirmed] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [queued, setQueued] = useState(false);
  const [suppressed, setSuppressed] = useState(false);
  const suppression = useRef(null);
  const alive = useRef(false);
  const command = useRef(null);
  const posting = useRef(false);
  useEffect(() => {
    alive.current = true;
    const controller = new AbortController();
    const urls = [];
    async function load() {
      try {
        const data = await shippingApi.buyerMessagePreview(operationKey, controller.signal);
        if (controller.signal.aborted) return;
        if (data.operation_key !== operationKey || data.message.order_id !== orderId) throw new Error('Message preview belongs to a different order.');
        setPreview(data);
        const images = await Promise.all(data.message.photos.map(async (photo) => {
          const blob = await shippingApi.buyerMessagePhoto(operationKey, photo.evidence_key, controller.signal);
          if (controller.signal.aborted) return null;
          const url = URL.createObjectURL(blob); urls.push(url);
          return { ...photo, url };
        }));
        if (!controller.signal.aborted) setPhotos(images);
      } catch (err) {
        if (controller.signal.aborted) return;
        if (err.status === 401 || err.status === 403) onAccessLost(err);
        else setError(err.message || 'Buyer message preview unavailable.');
      }
    }
    load();
    return () => { alive.current = false; controller.abort(); urls.forEach((url) => URL.revokeObjectURL(url)); };
  }, [operationKey, orderId, onAccessLost]);
  const complete = preview && photos.length === preview.message.photos.length && loaded.length === photos.length;
  async function queue() {
    if (posting.current || queued || suppressed || !preview?.can_queue || !complete || !confirmed) return;
    posting.current = true; setBusy(true); setError('');
    try {
      if (!command.current) {
        if (!window.crypto?.getRandomValues) throw new Error('Secure request IDs require a supported browser over HTTPS.');
        const bytes = new Uint8Array(16); window.crypto.getRandomValues(bytes);
        command.current = { request_key: Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join(''),
          payload_sha256: preview.payload_sha256, message_sha256: preview.message_sha256, review_confirmed: true };
      }
      // Preserve the SAME command after a lost HTTP response. This queues locally;
      // it never calls eBay from the browser and is not a provider resend button.
      await shippingApi.queueBuyerMessage(operationKey, command.current, csrfToken);
      if (alive.current) setQueued(true);
    } catch (err) {
      if (!alive.current) return;
      if (err.status === 401 || err.status === 403) onAccessLost(err);
      else setError(err.message || 'Queue outcome unknown. Retry this same queue request.');
    } finally { posting.current = false; if (alive.current) setBusy(false); }
  }
  async function suppress() {
    if (posting.current || suppressed || !preview) return;
    posting.current = true; setBusy(true); setError('');
    try {
      if (!suppression.current) {
        if (!window.crypto?.getRandomValues) throw new Error('Secure request IDs require a supported browser over HTTPS.');
        const bytes = new Uint8Array(16); window.crypto.getRandomValues(bytes);
        suppression.current = { request_key: Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join(''), payload_sha256: preview.payload_sha256 };
      }
      await shippingApi.suppressBuyerMessage(operationKey, suppression.current, csrfToken);
      if (alive.current) setSuppressed(true);
    } catch (err) {
      if (!alive.current) return;
      if (err.status === 401 || err.status === 403) onAccessLost(err);
      else setError(err.message || 'Suppression could not be recorded.');
    } finally { posting.current = false; if (alive.current) setBusy(false); }
  }
  return <Box sx={{ mt: 2, borderTop: 1, borderColor: 'divider', pt: 2 }}>
    <Typography variant="h6">Review one buyer message</Typography>
    <Alert severity="warning">This can contact a real buyer when the Production pilot is enabled. Check the recipient, wording and every photo. This message does not mark the package handed over.</Alert>
    {error && <Alert severity="error" sx={{ my: 1 }}>{error}</Alert>}
    {!preview && !error && <CircularProgress size={20} aria-label="Loading buyer message preview" />}
    {preview && <>
      <Typography>Order: {preview.message.order_id}</Typography>
      <Typography>Recipient: {preview.message.recipient_id}</Typography>
      <Typography>Anchor listing: {preview.message.item_id}</Typography>
      <Typography variant="caption">Template: {preview.message.template_version} · {preview.execution_mode}</Typography>
      <Typography fontWeight={600} sx={{ mt: 1 }}>{preview.message.subject}</Typography>
      <Typography sx={{ whiteSpace: 'pre-wrap' }}>{preview.message.body}</Typography>
      <Stack direction="row" gap={2} flexWrap="wrap" sx={{ my: 2 }}>
        {photos.map((photo) => <Box key={photo.evidence_key} sx={{ maxWidth: 320 }}>
          <Box component="img" src={photo.url} alt={`${photo.media_name}, package ${photo.shipment_id}`} sx={{ maxWidth: '100%', maxHeight: 280 }}
            onLoad={() => setLoaded((keys) => keys.includes(photo.evidence_key) ? keys : [...keys, photo.evidence_key])}
            onError={() => { setError('A photo could not be displayed. Close and reload this review.'); setLoaded((keys) => keys.filter((key) => key !== photo.evidence_key)); }} />
          <Typography variant="caption">Package {photo.shipment_id} · {photo.media_name}</Typography>
        </Box>)}
      </Stack>
      {!complete && <Typography variant="body2">All {preview.message.photos.length} approved photos must load before approval.</Typography>}
      {preview.reason && <Alert severity="info">{preview.reason.replaceAll('_', ' ')}. Review the operation; there is no automatic resend for an uncertain result.</Alert>}
      {!queued && !suppressed && preview.can_queue && <FormControlLabel control={<Checkbox checked={confirmed} disabled={busy || !complete} onChange={(event) => setConfirmed(event.target.checked)} />}
        label="I checked the exact recipient, wording and every buyer-safe photo." />}
      {suppressed ? <Alert severity="info">This unsent message is suppressed. No automatic send or retry is permitted for this operation.</Alert> : queued ? <Alert severity="success">Approval recorded and queued. Check External operations for the eBay result; this is not confirmation of delivery.</Alert> :
        <Button variant="contained" disabled={busy || !preview.can_queue || !complete || !confirmed || !csrfToken} onClick={queue}>
          {busy ? 'Recording approval…' : preview.execution_mode === 'REHEARSAL' ? 'Approve and queue simulation' : 'Approve and queue one buyer message'}
        </Button>}
    </>}
    {preview && !suppressed && preview.reason !== 'BUYER_MESSAGE_SUPPRESSED' && ['PENDING', 'BLOCKED', 'RETRY_WAIT'].includes(preview.status) &&
      <Button color="warning" disabled={busy || !csrfToken} onClick={suppress}>Suppress this unsent message</Button>}
    <Button sx={{ ml: 1 }} disabled={busy} onClick={onClose}>Close review</Button>
  </Box>;
}
